import { apiClient, apiNewClient } from '../apiClient';

export const getDeviceCountByCampaign = async (campaignId) => {
  try {
    const response = await apiClient.get(`/campaigns/${campaignId}/device-count`);
    return response.data.count;
  } catch (error) {
    console.error('Error fetching device count:', error);
    throw error;
  }
};

export const getDeviceCountsByCampaigns = async (campaignIds = []) => {
  try {
    const response = await apiClient.post('/campaigns/device-counts', { campaignIds });
    return response.data?.counts || {};
  } catch (error) {
    console.error('Error fetching device counts:', error);
    throw error;
  }
};

export const getDevicePlaylist = async (serialNumber) => {
  try {
    const response = await apiClient.get(`/devices/playlist/?serialNumber=${serialNumber}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching device playlist:', error);
    throw error;
  }
};

export const fetchDeviceSummary = async (filters) => {
  const response = await apiClient.post('/pmiDevices/devices/summary', filters);
  return response.data;
};

export const addElementsToPlaylist = async (playlistId, elements) => {
  const response = await apiClient.post(`/vxtPlaylists/${playlistId}/add-elements`, { elements });
  return response.data;
};

export const listCampaigns = async ({ activeOnly = false } = {}) => {
  const response = await apiClient.get(activeOnly ? '/campaigns/filtered' : '/campaigns');
  return response.data;
};

export const getCampaignById = async (campaignId) => {
  const response = await apiClient.get(`/campaigns/${campaignId}`);
  return response.data;
};

export const getCampaignImportedObjects = async (campaignId, { limit = 200, search = '' } = {}) => {
  const response = await apiClient.get(`/campaigns/${campaignId}/imported-objects`, {
    params: { limit, search },
  });
  return response.data;
};

export const getCampaignEligibleDevices = async (
  campaignId,
  {
    page = 1,
    limit = 100,
    search = '',
    importScope = 'auto',
    sortBy = 'lastSeen',
    sortOrder = 'desc',
  } = {}
) => {
  const response = await apiClient.get(`/campaigns/${campaignId}/eligible-devices`, {
    params: { page, limit, search, importScope, sortBy, sortOrder },
  });
  return response.data;
};

export const createCampaign = async (campaignData) => {
  const response = await apiClient.post('/campaigns/create', campaignData);
  return response.data;
};

export const updateCampaign = async (campaignId, campaignData) => {
  const response = await apiClient.put(`/campaigns/${campaignId}`, campaignData);
  return response.data;
};

export const deleteCampaign = async (campaignId) => {
  const response = await apiClient.delete(`/campaigns/${campaignId}`);
  return response.data;
};

export const addFilesToCampaign = async (campaignId, files) => {
  const response = await apiClient.put(`/campaigns/${campaignId}/files`, { files });
  return response.data;
};

export const fetchFileCampaigns = async (fileId) => {
  const response = await apiClient.get(`/campaigns/file/${fileId}/campaigns`);
  return response.data;
};

export const listCategorySchedules = async ({ segmentId = '', search = '' } = {}) => {
  const response = await apiClient.get('/categorySchedules', {
    params: { segmentId, search },
  });
  return response.data;
};

export const syncCategorySchedules = async (payload) => {
  const response = await apiClient.post('/categorySchedules/sync', payload);
  return response.data;
};

export const deleteCategorySchedule = async (scheduleId) => {
  const response = await apiClient.delete(`/categorySchedules/${scheduleId}`);
  return response.data;
};

export const checkFiles = async (fileHashes) => {
  const response = await apiClient.post('/media/check-files', { fileHashes });
  return response.data;
};

export const listFiles = async () => {
  const response = await apiClient.get('/media/list-files');
  return response.data;
};

export const updateFile = async (fileId, fileData) => {
  try {
    const response = await apiClient.put(`/media/${fileId}`, fileData);
    return response.data;
  } catch (error) {
    console.error('Error updating file:', error);
    throw error;
  }
};

export const deleteFile = async (fileId) => {
  try {
    const response = await apiClient.delete(`/media/${fileId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

export const getMediaDmxAnalysis = async (fileId) => {
  const response = await apiClient.get(`/media/${fileId}/dmx-analysis`);
  return response.data;
};

export const getMediaDmxAnalysisPreview = async (
  fileId,
  { fromFrame, toFrame, step, maxSamples } = {}
) => {
  const response = await apiClient.get(`/media/${fileId}/dmx-analysis/preview`, {
    params: { fromFrame, toFrame, step, maxSamples },
  });
  return response.data;
};

export const getMediaDmxAnalysisFrame = async (fileId, frameIndex) => {
  const response = await apiClient.get(`/media/${fileId}/dmx-analysis/frame/${frameIndex}`);
  return response.data;
};

export const requestMediaDmxAnalysis = async (fileId, { force = false } = {}) => {
  const response = await apiClient.post(`/media/${fileId}/dmx-analysis/request`, { force });
  return response.data;
};

export const fetchPlaylists = async () => {
  const response = await apiClient.get('/vxtPlaylists');
  return response.data;
};

export const createPlaylist = async (playlistData) => {
  const response = await apiClient.post('/vxtPlaylists/createPl', playlistData);
  return response.data;
};

export const updatePlaylist = async (playlistId, playlistData) => {
  const response = await apiClient.put(`/vxtPlaylists/${playlistId}`, playlistData);
  return response.data;
};

export const deleteElementFromPlaylist = async (playlistId, elementId) => {
  const response = await apiClient.delete(`/vxtPlaylists/${playlistId}/remove-element`, {
    data: { elementId },
  });
  return response.data.playlist;
};

export const getPlaylistFiles = async (playlistId) => {
  const response = await apiClient.get(`/vxtPlaylists/${playlistId}/files`);
  return response.data.files;
};

export const fetchCategories = async () => {
  const response = await apiClient.get('/category');
  return response.data;
};

export const createCategory = async (categoryData) => {
  const response = await apiClient.post('/category', categoryData);
  return response.data.category ?? response.data;
};

export const updateCategory = async (categoryId, categoryData) => {
  try {
    const response = await apiClient.put(`/category/${categoryId}`, categoryData);
    return response.data;
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

export const deleteCategory = async (categoryId) => {
  try {
    const response = await apiClient.delete(`/category/${categoryId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

export const listImports = async () => {
  const response = await apiClient.get('/importTypes');
  return response.data.importTypes;
};

export const getImportsData = async (file) => {
  const response = await apiClient.get(`/importTypes/import-data/${file}`);
  return response.data;
};

export const deleteImport = async (fileId) => {
  try {
    const response = await apiClient.delete(`/importTypes/${fileId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting import:', error);
    throw error;
  }
};

export const listCampaignImportBindings = async () => {
  const response = await apiNewClient.get('/data-import-bindings');
  return response.data;
};

export const getCampaignImportBindingById = async (id) => {
  const response = await apiNewClient.get(`/data-import-bindings/${id}`);
  return response.data;
};

export const createCampaignImportBinding = async (bindingData) => {
  const response = await apiNewClient.post('/data-import-bindings', bindingData);
  return response.data;
};

export const updateCampaignImportBinding = async (id, bindingData) => {
  const response = await apiNewClient.put(`/data-import-bindings/${id}`, bindingData);
  return response.data;
};

export const deleteCampaignImportBinding = async (id) => {
  const response = await apiNewClient.delete(`/data-import-bindings/${id}`);
  return response.data;
};

export const getImportTypeById = async (id) => {
  const response = await apiNewClient.get(`/importTypes/${id}`);
  return response.data;
};

export const getTargetCollectionFields = async (collectionName) => {
  const response = await apiNewClient.get(`/importTypes/target-fields/${collectionName}`);
  return response.data;
};

export const createFieldMapping = async (mappingData) => {
  const response = await apiNewClient.post('/import-field-mappings', mappingData);
  return response.data;
};

export const listFieldMappingsByImportType = async (importTypeId) => {
  const response = await apiNewClient.get(`/import-field-mappings/by-import-type/${importTypeId}`);
  return response.data;
};

export const deleteFieldMapping = async (mappingId) => {
  const response = await apiNewClient.delete(`/import-field-mappings/${mappingId}`);
  return response.data;
};
